/**
 * Selector unificado de "Método + Cuenta" para toda la app.
 * - direction='out' → "Método de pago" + "Cuenta origen" (gastos, compromisos, financiaciones, seguros)
 * - direction='in'  → "Método de cobro" + "Cuenta destino" (ingresos)
 *
 * Las cuentas se cargan SIEMPRE del mismo catálogo (/api/assets),
 * filtradas por tipos financieros líquidos.
 * Los métodos se cargan de /api/payment-methods (autoseed al primer acceso).
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

// Tipos de Activo considerados "cuentas / medios financieros" — usables como cuenta origen/destino
export const LIQUID_ASSET_TYPES = new Set([
  // Tipos oficiales V2.7
  "Bancos y Caja", "Tarjetas", "Wallets y Plataformas de Pago",
  // Legacy retro-compat
  "Banco", "Tarjeta", "Revolut", "Wise", "PayPal", "Efectivo",
  "Criptomonedas", "Binance", "Coinbase", "Fondos monetarios",
]);

const LIQUID_GROUP = "Dinero disponible";

const LABELS = {
  out: { method: "Método de pago", account: "Cuenta origen" },
  in: { method: "Método de cobro", account: "Cuenta destino" },
};

let _cache = null;
function useAccountsAndMethods() {
  const [state, setState] = useState(_cache);
  useEffect(() => {
    if (_cache) return;
    Promise.all([api.get("/assets"), api.get("/payment-methods"), api.get("/asset_types")])
      .then(([a, m, t]) => {
        const liquid = new Set(LIQUID_ASSET_TYPES);
        (t.data || []).forEach((at) => { if (at.group === LIQUID_GROUP) liquid.add(at.name); });
        _cache = {
          accounts: (a.data || []).filter((x) => liquid.has(x.type)).sort((x, y) => x.name.localeCompare(y.name)),
          methods: (m.data || []).filter((x) => x.active).sort((x, y) => x.name.localeCompare(y.name)),
        };
        setState(_cache);
      })
      .catch(() => setState({ accounts: [], methods: [] }));
  }, []);
  return state || { accounts: [], methods: [] };
}

export function refreshCatalogs() { _cache = null; }

/**
 * Uso: <MoneyMovementFields direction="out" values={form} onChange={(patch) => setForm({...form, ...patch})} testPrefix="new" />
 */
export default function MoneyMovementFields({ direction = "out", values, onChange, testPrefix = "mm", required = false, className = "" }) {
  const { accounts, methods } = useAccountsAndMethods();
  const l = LABELS[direction] || LABELS.out;
  const req = required ? " *" : "";
  return (
    <div className={`grid grid-cols-2 gap-3 ${className}`}>
      <div>
        <label className="text-xs text-muted-foreground">{l.account}{req}</label>
        <Select
          value={values.account_id || "none"}
          onValueChange={(v) => onChange({ account_id: v === "none" ? "" : v })}
        >
          <SelectTrigger data-testid={`${testPrefix}-account`}><SelectValue placeholder="Elige cuenta…" /></SelectTrigger>
          <SelectContent>
            {!required && <SelectItem value="none">Sin especificar</SelectItem>}
            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.type ? ` · ${a.type}` : ""}</SelectItem>)}
            {accounts.length === 0 && <SelectItem value="__empty" disabled>Crea una cuenta en Catálogo Maestro › Activos</SelectItem>}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs text-muted-foreground">{l.method}</label>
        <Select
          value={values.payment_method_id || "none"}
          onValueChange={(v) => onChange({ payment_method_id: v === "none" ? "" : v })}
        >
          <SelectTrigger data-testid={`${testPrefix}-method`}><SelectValue placeholder="Elige…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sin especificar</SelectItem>
            {methods.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
